import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  PictureAsPdf as PictureAsPdfIcon,
  DateRange as DateIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  DeleteOutline as DeleteIcon,
  EventAvailable as PostAttendanceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  loadDovolenaRequests,
  createDovolenaRequest,
  downloadDovolenaPdf,
  loadVacationSupervisors,
  updateDovolenaRequestStatus,
  loadDovolenaByShareToken,
  deleteDovolenaRequest,
  setDovolenaPersonalistaProcessed,
  postDovolenaToAttendance,
  loadPersonalNumber,
  getSetting,
  setSetting,
  loadBuildings,
  loadSchoolEmployees,
} from '../lib/apiPhpAdapter';
import type {
  DovolenaRequest,
  DovolenaRequestDay,
  User,
  DovolenaDashboard,
  DovolenaRequestType,
  Building,
} from '../types';
import {
  DOVOLENA_MODULE_TITLE,
  DOVOLENA_REQUEST_TYPES,
  dovolenaDuvodPlaceholder,
  dovolenaDuvodFieldLabel,
  dovolenaRequestTypeRequiresDuvod,
  dovolenaRequestTypeRequiresEducation,
  dovolenaRequestTypeRequiresSchoolEvent,
  labelDovolenaEmploymentRelation,
  labelDovolenaRequestType,
} from '../lib/dovolenaRequestType';
import { canCreateOnBehalf, canDeleteOnBehalfRequest } from '../lib/onBehalfAccess';
import { EmployeeUserPicker } from '../components/EmployeeUserPicker';
import { DovolenaRequestsToolbar } from '../components/DovolenaRequestsToolbar';
import DovolenaDaysCalendar, {
  formatDovolenaDayHours,
  isWeekendLocal,
  parseYmdLocal,
} from '../components/DovolenaDaysCalendar';
import {
  filterAndSortDovolenaRequests,
  type DovolenaSortKey,
} from '../lib/dovolenaListFilters';
import { labelDovolenaUiRole, type DovolenaUiRole } from '../lib/dovolenaRoles';

interface DovolenaViewProps {
  user: User;
  /** Role vybraná na výběru karet (jako Žádanky o nákup). */
  uiRole?: DovolenaUiRole;
  /** Otevření z odkazu (GET /dovolena/share/…) */
  shareToken?: string;
}

const emptyDashboard: DovolenaDashboard = {
  mine: [],
  pendingApproval: [],
  approvedByMe: [],
  rejectedByMe: [],
  allRequests: null,
  deletedRequests: null,
  suplovaniRequests: null,
  config: { suplovani_notify_available: false },
};

function requestWantsSuplovani(req: DovolenaRequest): boolean {
  return Boolean(req.notify_suplovani || req.notify_doprovod);
}

const DOVOLENA_SUPERVISOR_ROLE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'vedouci', label: 'Vedoucí' },
  { id: 'director', label: 'Ředitel' },
  { id: 'financist', label: 'Hlavní ekonom' },
  { id: 'referent_eko', label: 'Referent EKO' },
  { id: 'personalista', label: 'Personalista' },
  { id: 'admin:spravce_budov', label: 'Správce budov (Admin)' },
  { id: 'admin:spravce_it', label: 'Správce IT (Admin)' },
  { id: 'vedouci_executor', label: 'Vedoucí řešitel' },
  { id: 'referent', label: 'Referent' },
  { id: 'property_referent', label: 'Majetkový referent' },
  { id: 'spravce_rozvrhu', label: 'Správce rozvrhu' },
  { id: 'spravce_jidelny', label: 'Správce jídelny' },
  { id: 'zocmos', label: 'ZOCMOS' },
  { id: 'zastupce_reditele', label: 'Zástupce ředitele' },
];

const DEFAULT_DOVOLENA_SUPERVISOR_ROLES = ['vedouci', 'director', 'financist', 'admin:spravce_budov'];

const DovolenaView: React.FC<DovolenaViewProps> = ({ user, uiRole = 'zadatel', shareToken }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<DovolenaDashboard>(emptyDashboard);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareRequest, setShareRequest] = useState<DovolenaRequest | null>(null);
  const [shareLoadError, setShareLoadError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [approvalTab, setApprovalTab] = useState(0);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [suplovaniEmail, setSuplovaniEmail] = useState('');
  const [savingSuplovaniEmail, setSavingSuplovaniEmail] = useState(false);
  const [suplovaniEmailMsg, setSuplovaniEmailMsg] = useState<string | null>(null);
  const [supervisorRoleIds, setSupervisorRoleIds] = useState<string[]>([]);
  const [savingSupervisorRoles, setSavingSupervisorRoles] = useState(false);
  const [supervisorRolesMsg, setSupervisorRolesMsg] = useState<string | null>(null);
  /** building_id → employee picker user id */
  const [buildingAttendanceAdmins, setBuildingAttendanceAdmins] = useState<Record<string, string>>({});
  const [attendanceEmployees, setAttendanceEmployees] = useState<User[]>([]);
  const [savingAttendanceAdmins, setSavingAttendanceAdmins] = useState(false);
  const [attendanceAdminsMsg, setAttendanceAdminsMsg] = useState<string | null>(null);
  const [mineNameQuery, setMineNameQuery] = useState('');
  const [mineCreatedFrom, setMineCreatedFrom] = useState('');
  const [mineCreatedTo, setMineCreatedTo] = useState('');
  const [mineLeaveFrom, setMineLeaveFrom] = useState('');
  const [mineLeaveTo, setMineLeaveTo] = useState('');
  const [mineSortKey, setMineSortKey] = useState<DovolenaSortKey>('date_from');
  const [mineSortDir, setMineSortDir] = useState<'asc' | 'desc'>('desc');

  const [approvalNameQuery, setApprovalNameQuery] = useState('');
  const [approvalCreatedFrom, setApprovalCreatedFrom] = useState('');
  const [approvalCreatedTo, setApprovalCreatedTo] = useState('');
  const [approvalLeaveFrom, setApprovalLeaveFrom] = useState('');
  const [approvalLeaveTo, setApprovalLeaveTo] = useState('');
  const [approvalSortKey, setApprovalSortKey] = useState<DovolenaSortKey>('date_from');
  const [approvalSortDir, setApprovalSortDir] = useState<'asc' | 'desc'>('desc');

  /** Jen záložka „Všechny“ */
  const [allListTypeFilter, setAllListTypeFilter] = useState<'all' | DovolenaRequestType>('all');
  const [allListStatusFilter, setAllListStatusFilter] = useState<'all' | 'new' | 'approved' | 'rejected'>('all');
  const [allListProcessedFilter, setAllListProcessedFilter] = useState<'all' | 'processed' | 'unprocessed'>('all');
  const [deleteTarget, setDeleteTarget] = useState<DovolenaRequest | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [decisionTarget, setDecisionTarget] = useState<{
    req: DovolenaRequest
    action: 'approve' | 'reject'
  } | null>(null);
  const [settingsListTab, setSettingsListTab] = useState(0);

  const isItAdmin = user.role === 'admin' && (user as any).admin_type === 'spravce_it';

  const isPersonalistaRole = uiRole === 'personalista';
  const isVedouciRole = uiRole === 'vedouci';
  const isZadatelRole = uiRole === 'zadatel';
  const isSpravceRozvrhuRole = uiRole === 'spravce_rozvrhu';
  const isNastaveniRole = uiRole === 'nastaveni' && isItAdmin;

  /** Schvalování cizích žádostí z přehledu „Všechny“ — jen ředitel a IT. */
  const canApprovePrivilegedInAllTab =
    user.role === 'director' ||
    (user.role === 'admin' && (user as { admin_type?: string }).admin_type === 'spravce_it');

  const canSubmitOnBehalf = uiRole === 'vedouci' && canCreateOnBehalf(user);
  const showCreateButton = uiRole === 'zadatel' || uiRole === 'vedouci';

  const suplovaniNotifyAvailable = Boolean(dashboard.config?.suplovani_notify_available);
  const [proxyEmployee, setProxyEmployee] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    request_type: 'dovolena' as DovolenaRequestType,
    duvod: '',
    school_event_name: '',
    education_type: '' as '' | 'akreditované' | 'neakreditované',
    education_name: '',
    accreditation_number: '',
    notify_suplovani: true,
    personal_number: '',
    requester_name: user.name || '',
    workplace: '',
    employment_relation: 'hlavni' as 'hlavni' | 'vedlejsi',
    year: String(new Date().getFullYear()),
    date_from: '',
    date_to: '',
    total_hours: 8,
    supervisor_id: '',
  });
  const [selectedDays, setSelectedDays] = useState<DovolenaRequestDay[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());

  const fetchSupervisors = async () => {
    try {
      setLoadingSupervisors(true);
      const data = await loadVacationSupervisors();
      setSupervisors(data);
    } catch (err) {
      console.error('Failed to load supervisors:', err);
    } finally {
      setLoadingSupervisors(false);
    }
  };

  const fetchBuildings = async () => {
    try {
      setLoadingBuildings(true);
      const data = await loadBuildings();
      setBuildings(data);
    } catch (err) {
      console.error('Failed to load buildings:', err);
    } finally {
      setLoadingBuildings(false);
    }
  };

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const data = await loadDovolenaRequests();
      setDashboard(data);
      setError(null);
    } catch (err: unknown) {
      setError('Nepodařilo se načíst žádosti.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchSupervisors();
    fetchBuildings();
  }, [fetchRequests]);

  // Deep-link z ranního digestu: ?id=
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id || loading) return;
    if (deepLinkHandled.current === id) return;
    deepLinkHandled.current = id;

    const pools: DovolenaRequest[] = [
      ...(dashboard.pendingApproval || []),
      ...(dashboard.approvedByMe || []),
      ...(dashboard.rejectedByMe || []),
      ...(dashboard.allRequests || []),
      ...(dashboard.mine || []),
    ];
    const found = pools.find((r) => r.id === id);

    if (found) {
      setHighlightId(id);
      if (isPersonalistaRole) {
        setAllListStatusFilter(found.status === 'new' ? 'new' : 'all');
        setApprovalTab(0);
      } else if (isVedouciRole) {
        if (found.status === 'new') setApprovalTab(0);
        else if (found.status === 'approved') setApprovalTab(1);
        else setApprovalTab(2);
      }
      window.setTimeout(() => {
        const el = document.getElementById(`dovolena-req-${id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 150);
    } else {
      setError('Žádost nenalezena nebo již byla zpracována.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  }, [
    searchParams,
    loading,
    dashboard,
    isPersonalistaRole,
    isVedouciRole,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!isNastaveniRole) return;
    let cancelled = false;
    (async () => {
      try {
        const email =
          (await getSetting('dovolena_suplovani_notify_email')) ||
          (await getSetting('dovolena_doprovod_notify_email'));
        if (!cancelled) setSuplovaniEmail(email || '');
      } catch {
        if (!cancelled) setSuplovaniEmail('');
      }
      try {
        const rolesJson = await getSetting('vacation_supervisor_roles');
        if (cancelled) return;
        if (rolesJson) {
          try {
            const parsed = JSON.parse(rolesJson);
            setSupervisorRoleIds(Array.isArray(parsed) ? parsed.map(String) : DEFAULT_DOVOLENA_SUPERVISOR_ROLES);
          } catch {
            setSupervisorRoleIds(DEFAULT_DOVOLENA_SUPERVISOR_ROLES);
          }
        } else {
          setSupervisorRoleIds(DEFAULT_DOVOLENA_SUPERVISOR_ROLES);
        }
      } catch {
        if (!cancelled) setSupervisorRoleIds(DEFAULT_DOVOLENA_SUPERVISOR_ROLES);
      }
      try {
        const adminsJson = await getSetting('dovolena_building_attendance_admins');
        if (cancelled) return;
        if (adminsJson) {
          try {
            const parsed = JSON.parse(adminsJson);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
              const map: Record<string, string> = {};
              for (const [bid, uid] of Object.entries(parsed as Record<string, unknown>)) {
                const id = String(uid || '').trim();
                if (bid && id) map[String(bid)] = id;
              }
              setBuildingAttendanceAdmins(map);
            } else {
              setBuildingAttendanceAdmins({});
            }
          } catch {
            setBuildingAttendanceAdmins({});
          }
        } else {
          setBuildingAttendanceAdmins({});
        }
      } catch {
        if (!cancelled) setBuildingAttendanceAdmins({});
      }
      try {
        const emps = await loadSchoolEmployees();
        if (!cancelled) setAttendanceEmployees(Array.isArray(emps) ? emps : []);
      } catch {
        if (!cancelled) setAttendanceEmployees([]);
      }
      try {
        const blds = await loadBuildings();
        if (!cancelled) setBuildings(Array.isArray(blds) ? blds : []);
      } catch {
        /* buildings already loaded for form; ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNastaveniRole]);

  const handleSaveSuplovaniEmail = async () => {
    const trimmed = suplovaniEmail.trim();
    if (trimmed !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setSuplovaniEmailMsg(
        'Zadejte platnou e-mailovou adresu nebo nechte pole prázdné (vypne možnost suplování).',
      );
      return;
    }
    try {
      setSavingSuplovaniEmail(true);
      setSuplovaniEmailMsg(null);
      await setSetting(
        'dovolena_suplovani_notify_email',
        trimmed,
        'E-mail osoby zajišťující suplování po schválení žádosti o nepřítomnost',
      );
      setSuplovaniEmailMsg(
        trimmed
          ? 'E-mail pro suplování byl uložen.'
          : 'E-mail byl vymazán — volba suplování v žádostech je vypnutá.',
      );
      await fetchRequests();
    } catch {
      setSuplovaniEmailMsg('Chyba při ukládání e-mailu pro suplování.');
    } finally {
      setSavingSuplovaniEmail(false);
    }
  };

  const toggleSupervisorRole = (roleId: string) => {
    setSupervisorRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId],
    );
  };

  const handleSaveSupervisorRoles = async () => {
    try {
      setSavingSupervisorRoles(true);
      setSupervisorRolesMsg(null);
      await setSetting(
        'vacation_supervisor_roles',
        JSON.stringify(supervisorRoleIds),
        'Seznam rolí, které se zobrazují jako nadřízení v žádostech o dovolenou, neplaceném volnu a samostudiu',
      );
      setSupervisorRolesMsg('Nastavení vedoucích bylo uloženo.');
      await fetchSupervisors();
    } catch {
      setSupervisorRolesMsg('Chyba při ukládání nastavení vedoucích.');
    } finally {
      setSavingSupervisorRoles(false);
    }
  };

  const setBuildingAttendanceAdmin = (buildingId: string, userId: string | null) => {
    setBuildingAttendanceAdmins((prev) => {
      const next = { ...prev };
      if (!userId) {
        delete next[buildingId];
      } else {
        next[buildingId] = userId;
      }
      return next;
    });
  };

  const handleSaveAttendanceAdmins = async () => {
    try {
      setSavingAttendanceAdmins(true);
      setAttendanceAdminsMsg(null);
      await setSetting(
        'dovolena_building_attendance_admins',
        JSON.stringify(buildingAttendanceAdmins),
        'Správci docházky podle budovy — e-mail po schválení žádosti o nepřítomnost',
      );
      setAttendanceAdminsMsg('Nastavení správců docházky bylo uloženo.');
    } catch {
      setAttendanceAdminsMsg('Chyba při ukládání správců docházky.');
    } finally {
      setSavingAttendanceAdmins(false);
    }
  };
  useEffect(() => {
    const targetId = proxyEmployee?.id;
    let cancelled = false;
    loadPersonalNumber(targetId)
      .then((pn) => {
        if (!cancelled) {
          setFormData((prev) => ({ ...prev, personal_number: pn ?? '' }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFormData((prev) => ({ ...prev, personal_number: '' }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, proxyEmployee?.id]);

  useEffect(() => {
    if (!shareToken) {
      setShareRequest(null);
      setShareLoadError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await loadDovolenaByShareToken(shareToken);
        if (!cancelled) {
          setShareRequest(r);
          setShareLoadError(null);
        }
      } catch {
        if (!cancelled) {
          setShareRequest(null);
          setShareLoadError('Odkaz je neplatný nebo k této žádosti nemáte přístup.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareToken]);

  useEffect(() => {
    setApprovalTab(0);
  }, [uiRole]);

  useEffect(() => {
    if (isPersonalistaRole) {
      const max = isItAdmin ? 1 : 0;
      if (approvalTab > max) setApprovalTab(0);
    } else if (isVedouciRole && approvalTab > 2) {
      setApprovalTab(0);
    }
  }, [isPersonalistaRole, isVedouciRole, isItAdmin, approvalTab]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDays.length === 0) {
      alert('Vyberte alespoň jeden pracovní den v kalendáři.');
      return;
    }
    if (dovolenaRequestTypeRequiresDuvod(formData.request_type) && !formData.duvod.trim()) {
      alert(
        formData.request_type === 'jine'
          ? 'U typu Jiné je poznámka / důvod povinný.'
          : 'U tohoto typu žádosti je pole Důvod povinné.',
      );
      return;
    }
    if (
      dovolenaRequestTypeRequiresSchoolEvent(formData.request_type) &&
      !formData.school_event_name.trim()
    ) {
      alert('U akce školy je povinný název akce.');
      return;
    }
    if (dovolenaRequestTypeRequiresEducation(formData.request_type)) {
      if (!formData.education_type) {
        alert('Vyberte typ vzdělávání.');
        return;
      }
      if (!formData.education_name.trim()) {
        alert('Uveďte název vzdělávání.');
        return;
      }
      if (formData.education_type === 'akreditované' && !formData.accreditation_number.trim()) {
        alert('U akreditovaného vzdělávání je povinné číslo akreditace.');
        return;
      }
    }
    const sorted = [...selectedDays]
      .filter((d) => !isWeekendLocal(parseYmdLocal(d.day_date)))
      .sort((a, b) => a.day_date.localeCompare(b.day_date));
    if (sorted.length === 0) {
      alert('Víkendy nelze vybrat. Vyberte alespoň jeden pracovní den.');
      return;
    }
    if (sorted.some((d) => d.hours < 8 && (d.start_hour == null || d.start_hour < 6))) {
      alert('U dnů s méně než 8 hodinami uveďte, od které hodiny volno začíná.');
      return;
    }
    const totalHours = sorted.reduce((s, d) => s + d.hours, 0);
    try {
      setSubmitting(true);
      const requiresDuvod = dovolenaRequestTypeRequiresDuvod(formData.request_type);
      const requiresEvent = dovolenaRequestTypeRequiresSchoolEvent(formData.request_type);
      const requiresEdu = dovolenaRequestTypeRequiresEducation(formData.request_type);
      const duvodTrimmed = formData.duvod.trim();
      const payload: Record<string, unknown> = {
        ...formData,
        year: Number.parseInt(formData.year, 10) || parseYmdLocal(sorted[0].day_date).getFullYear(),
        date_from: sorted[0].day_date,
        date_to: sorted[sorted.length - 1].day_date,
        total_hours: totalHours,
        days: sorted,
        duvod: requiresDuvod || duvodTrimmed !== '' ? duvodTrimmed : '',
        school_event_name: requiresEvent ? formData.school_event_name.trim() : null,
        education_type: requiresEdu ? formData.education_type : null,
        education_name: requiresEdu ? formData.education_name.trim() : null,
        accreditation_number:
          requiresEdu && formData.education_type === 'akreditované'
            ? formData.accreditation_number.trim()
            : null,
        notify_suplovani:
          suplovaniNotifyAvailable && formData.notify_suplovani ? true : false,
        requester_name: proxyEmployee?.name ?? formData.requester_name,
      };
      if (proxyEmployee && proxyEmployee.id !== user.id) {
        payload.requester_id = proxyEmployee.id;
      }
      await createDovolenaRequest(payload);
      setOpenDialog(false);
      setProxyEmployee(null);
      setSelectedDays([]);
      fetchRequests();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Neznámá chyba';
      alert('Chyba při odesílání žádosti: ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const mineFilterParams = useMemo(
    () => ({
      nameQuery: mineNameQuery,
      createdFrom: mineCreatedFrom,
      createdTo: mineCreatedTo,
      leaveFrom: mineLeaveFrom,
      leaveTo: mineLeaveTo,
      sortKey: mineSortKey,
      sortDir: mineSortDir,
    }),
    [
      mineNameQuery,
      mineCreatedFrom,
      mineCreatedTo,
      mineLeaveFrom,
      mineLeaveTo,
      mineSortKey,
      mineSortDir,
    ],
  );

  const approvalFilterParams = useMemo(
    () => ({
      nameQuery: approvalNameQuery,
      createdFrom: approvalCreatedFrom,
      createdTo: approvalCreatedTo,
      leaveFrom: approvalLeaveFrom,
      leaveTo: approvalLeaveTo,
      sortKey: approvalSortKey,
      sortDir: approvalSortDir,
    }),
    [
      approvalNameQuery,
      approvalCreatedFrom,
      approvalCreatedTo,
      approvalLeaveFrom,
      approvalLeaveTo,
      approvalSortKey,
      approvalSortDir,
    ],
  );

  const filterMineRows = useCallback(
    (rows: DovolenaRequest[]) => filterAndSortDovolenaRequests(rows, mineFilterParams),
    [mineFilterParams],
  );

  const filterApprovalRows = useCallback(
    (rows: DovolenaRequest[]) => filterAndSortDovolenaRequests(rows, approvalFilterParams),
    [approvalFilterParams],
  );

  const ownRequests = useMemo(
    () => dashboard.mine.filter((r) => String(r.requester_id) === String(user.id)),
    [dashboard.mine, user.id],
  );

  const onBehalfRequests = useMemo(
    () =>
      dashboard.mine.filter(
        (r) =>
          String(r.created_by_id ?? '') === String(user.id) &&
          String(r.requester_id) !== String(user.id),
      ),
    [dashboard.mine, user.id],
  );

  const mineRequestsFiltered = useMemo(
    () => filterMineRows(isZadatelRole ? ownRequests : dashboard.mine),
    [isZadatelRole, ownRequests, dashboard.mine, filterMineRows],
  );

  const onBehalfRequestsFiltered = useMemo(
    () => filterMineRows(onBehalfRequests),
    [onBehalfRequests, filterMineRows],
  );

  const allRequestsFiltered = useMemo(
    () =>
      filterAndSortDovolenaRequests(dashboard.allRequests ?? [], {
        ...approvalFilterParams,
        typeFilter: allListTypeFilter,
        statusFilter: allListStatusFilter,
        processedFilter: allListProcessedFilter,
        personalistaView:
          isPersonalistaRole || isNastaveniRole || user.role === 'personalista',
      }),
    [
      dashboard.allRequests,
      approvalFilterParams,
      allListTypeFilter,
      allListStatusFilter,
      allListProcessedFilter,
      isPersonalistaRole,
      isNastaveniRole,
      user.role,
    ],
  );

  const deletedRequestsFiltered = useMemo(
    () =>
      filterAndSortDovolenaRequests(dashboard.deletedRequests ?? [], {
        ...approvalFilterParams,
        typeFilter: allListTypeFilter,
        statusFilter: allListStatusFilter,
      }),
    [
      dashboard.deletedRequests,
      approvalFilterParams,
      allListTypeFilter,
      allListStatusFilter,
    ],
  );

  const suplovaniRequestsFiltered = useMemo(
    () => filterApprovalRows(dashboard.suplovaniRequests ?? []),
    [dashboard.suplovaniRequests, filterApprovalRows],
  );

  const handleDownload = async (id: string) => {
    try {
      await downloadDovolenaPdf(id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Neznámá chyba';
      alert('Chyba při stahování: ' + msg);
    }
  };

  const openDecisionDialog = (req: DovolenaRequest, action: 'approve' | 'reject') => {
    setDecisionTarget({ req, action });
  };

  const handleDecisionConfirm = async () => {
    if (!decisionTarget) return;
    const { req, action } = decisionTarget;
    try {
      setDecidingId(req.id);
      await updateDovolenaRequestStatus(req.id, action);
      setDecisionTarget(null);
      await fetchRequests();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Akce se nezdařila';
      alert(msg);
    } finally {
      setDecidingId(null);
    }
  };

  const handlePersonalistaProcessed = async (id: string, processed: boolean) => {
    try {
      setDecidingId(id);
      await setDovolenaPersonalistaProcessed(id, processed);
      // Neznovu-načítat celý přehled — jinak se tabulka "skočí" (scroll / re-render).
      // Stačí lokálně upravit řádek.
      setDashboard((prev) => {
        if (!prev.allRequests) return prev;
        const nextAll = prev.allRequests.map((r) => {
          if (String(r.id) !== String(id)) return r;
          return {
            ...r,
            personalista_processed_at: processed ? new Date().toISOString() : null,
            personalista_processed_by: processed ? user.id : null,
          };
        });
        return { ...prev, allRequests: nextAll };
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Akce se nezdařila';
      alert(msg);
    } finally {
      setDecidingId(null);
    }
  };

  const patchRequestInDashboard = (id: string, patch: Partial<DovolenaRequest>) => {
    setDashboard((prev) => {
      const patchList = (list: DovolenaRequest[] | null | undefined) =>
        !list
          ? list
          : list.map((r) => (String(r.id) === String(id) ? { ...r, ...patch } : r));
      return {
        ...prev,
        allRequests: patchList(prev.allRequests) ?? null,
        approvedByMe: patchList(prev.approvedByMe) ?? [],
        mine: patchList(prev.mine) ?? [],
        pendingApproval: patchList(prev.pendingApproval) ?? [],
        rejectedByMe: patchList(prev.rejectedByMe) ?? [],
        deletedRequests: patchList(prev.deletedRequests ?? null) ?? null,
        suplovaniRequests: patchList(prev.suplovaniRequests ?? null) ?? null,
      };
    });
  };

  const handlePostToAttendance = async (req: DovolenaRequest) => {
    const already = Boolean(req.attendance_posted_at);
    const ok = window.confirm(
      already
        ? 'Žádost už byla zapsána do docházky. Zapsat znovu a přepsat dny?'
        : 'Zapsat schválené dny této žádosti do docházkového systému?',
    );
    if (!ok) return;
    try {
      setDecidingId(req.id);
      const res = await postDovolenaToAttendance(req.id, already);
      const errN = res.errors?.length ?? 0;
      alert(
        res.already_posted && !already
          ? 'Již zapsáno dříve.'
          : `Docházka: zapsáno ${res.written} dnů` +
              (res.skipped ? `, přeskočeno ${res.skipped}` : '') +
              (errN ? `\n\nUpozornění:\n${res.errors.slice(0, 8).join('\n')}` : ''),
      );
      patchRequestInDashboard(req.id, {
        attendance_posted_at: new Date().toISOString(),
        attendance_posted_by: user.id,
        personalista_processed_at: new Date().toISOString(),
        personalista_processed_by: user.id,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Zápis do docházky selhal';
      alert(msg);
    } finally {
      setDecidingId(null);
    }
  };

  const openDeleteDialog = (req: DovolenaRequest) => {
    setDeleteTarget(req);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeletingId(deleteTarget.id);
      await deleteDovolenaRequest(deleteTarget.id);
      setDeleteTarget(null);
      await fetchRequests();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Mazání se nezdařilo';
      alert(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusChip = (status: string) => {
    const chipSx = {
      maxWidth: '100%',
      height: 24,
      '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
    };
    switch (status) {
      case 'approved':
        return <Chip label="Schváleno" color="success" size="small" sx={chipSx} />;
      case 'rejected':
        return <Chip label="Zamítnuto" color="error" size="small" sx={chipSx} />;
      default:
        return (
          <Chip
            label="Čeká"
            title="Čeká na schválení"
            color="primary"
            variant="outlined"
            size="small"
            sx={chipSx}
          />
        );
    }
  };

  const renderRequestTable = (
    rows: DovolenaRequest[],
    options: {
      colSpanEmpty: number;
      emptyLabel: string;
      showRequester?: boolean;
      showSupervisor?: boolean;
      showApprovalActions?: boolean;
      showPersonalistaProcessed?: boolean;
      showPostToAttendance?: boolean;
      showDeleteForOwner?: boolean;
      showDeletedAt?: boolean;
    }
  ) => {
    const {
      colSpanEmpty,
      emptyLabel,
      showRequester = false,
      showSupervisor = false,
      showApprovalActions = false,
      showPersonalistaProcessed = false,
      showPostToAttendance = false,
      showDeleteForOwner = false,
      showDeletedAt = false,
    } = options;

    const canDeleteRequest = (req: DovolenaRequest) =>
      showDeleteForOwner && canDeleteOnBehalfRequest(user, req, isItAdmin);

    const ellipsisCellSx = {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: 0,
    } as const;

    const actionBtnSx = {
      width: 40,
      height: 40,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1.25,
      bgcolor: 'background.paper',
      '& .MuiSvgIcon-root': { fontSize: 22 },
    } as const;

    return (
      <Table
        size="small"
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          maxWidth: '100%',
        }}
      >
        <TableHead sx={{ bgcolor: 'grey.100' }}>
          <TableRow>
            {showPersonalistaProcessed && (
              <TableCell sx={{ fontWeight: 'bold', width: 52 }}>Zprac.</TableCell>
            )}
            {showRequester && (
              <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Žadatel</TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Typ žádosti</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 88 }}>Datum od</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 88 }}>Datum do</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 56 }}>Hodin</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Budova</TableCell>
            {showSupervisor && (
              <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Vedoucí</TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold', width: 88 }}>Stav</TableCell>
            {showDeletedAt && (
              <TableCell sx={{ fontWeight: 'bold', width: 110 }}>Smazáno</TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold', width: 108 }} align="right">
              Akce
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpanEmpty} align="center" sx={{ py: 3 }}>
                {emptyLabel}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((req) => (
              <TableRow
                key={req.id}
                id={`dovolena-req-${req.id}`}
                hover
                sx={
                  highlightId === req.id
                    ? {
                        bgcolor: 'rgba(20, 184, 166, 0.18)',
                        outline: '2px solid #0d9488',
                        outlineOffset: '-2px',
                      }
                    : showPersonalistaProcessed && !req.personalista_processed_at
                      ? { bgcolor: 'rgba(255, 193, 7, 0.10)' }
                      : undefined
                }
              >
                {showPersonalistaProcessed && (
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={Boolean(req.personalista_processed_at)}
                      disabled={decidingId === req.id}
                      onChange={(e) => handlePersonalistaProcessed(req.id, e.target.checked)}
                      title={req.personalista_processed_at ? 'Označeno jako zpracováno' : 'Nezpracováno'}
                    />
                  </TableCell>
                )}
                {showRequester && (
                  <TableCell sx={ellipsisCellSx} title={req.requester_name || undefined}>
                    {req.requester_name}
                    {req.created_by_name && req.created_by_id !== req.requester_id && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="info.main"
                        sx={ellipsisCellSx}
                      >
                        Podal(a): {req.created_by_name}
                      </Typography>
                    )}
                  </TableCell>
                )}
                <TableCell
                  sx={ellipsisCellSx}
                  title={labelDovolenaRequestType(String(req.request_type || 'dovolena'))}
                >
                  {labelDovolenaRequestType(String(req.request_type || 'dovolena'))}
                  {requestWantsSuplovani(req) && (
                    <Chip label="Supl." size="small" color="info" sx={{ ml: 0.5 }} />
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>
                  {format(parseYmdLocal(String(req.date_from).slice(0, 10)), 'dd.MM.yyyy')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>
                  {format(parseYmdLocal(String(req.date_to).slice(0, 10)), 'dd.MM.yyyy')}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8125rem' }}>
                  {req.days && req.days.length > 0 ? (
                    <span
                      title={req.days
                        .map((d) => `${format(parseYmdLocal(d.day_date), 'dd.MM.yyyy')} (${formatDovolenaDayHours(d)})`)
                        .join(', ')}
                    >
                      {req.total_hours}
                    </span>
                  ) : (
                    req.total_hours
                  )}
                </TableCell>
                <TableCell sx={ellipsisCellSx} title={req.workplace || undefined}>
                  {req.workplace}
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={ellipsisCellSx}
                  >
                    {labelDovolenaEmploymentRelation(String(req.employment_relation || 'hlavni'))}
                  </Typography>
                </TableCell>
                {showSupervisor && (
                  <TableCell sx={ellipsisCellSx} title={req.supervisor_name || undefined}>
                    {req.supervisor_name || '-'}
                  </TableCell>
                )}
                <TableCell>{getStatusChip(req.status)}</TableCell>
                {showDeletedAt && (
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                    {req.deleted_at
                      ? format(new Date(req.deleted_at), 'dd.MM.yyyy HH:mm')
                      : '—'}
                  </TableCell>
                )}
                <TableCell
                  align="right"
                  sx={{ width: 108, py: 0.75, px: 0.5, verticalAlign: 'middle' }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '40px 40px',
                      gap: '6px',
                      justifyContent: 'end',
                      width: 'fit-content',
                      ml: 'auto',
                    }}
                  >
                    <Tooltip title="Stáhnout PDF">
                      <IconButton
                        color="secondary"
                        onClick={() => handleDownload(req.id)}
                        aria-label="Stáhnout PDF"
                        sx={actionBtnSx}
                      >
                        <PictureAsPdfIcon />
                      </IconButton>
                    </Tooltip>
                    {canDeleteRequest(req) && (
                      <Tooltip title="Smazat žádost">
                        <span>
                          <IconButton
                            color="error"
                            onClick={() => openDeleteDialog(req)}
                            disabled={deletingId === req.id}
                            aria-label="Smazat žádost"
                            sx={actionBtnSx}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {showApprovalActions && req.status === 'new' && (
                      <>
                        <Tooltip title="Schválit">
                          <span>
                            <IconButton
                              color="success"
                              disabled={decidingId === req.id}
                              onClick={() => openDecisionDialog(req, 'approve')}
                              aria-label="Schválit"
                              sx={{
                                ...actionBtnSx,
                                borderColor: 'success.main',
                                bgcolor: 'rgba(46, 125, 50, 0.08)',
                              }}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Zamítnout">
                          <span>
                            <IconButton
                              color="error"
                              disabled={decidingId === req.id}
                              onClick={() => openDecisionDialog(req, 'reject')}
                              aria-label="Zamítnout"
                              sx={{
                                ...actionBtnSx,
                                borderColor: 'error.main',
                                bgcolor: 'rgba(211, 47, 47, 0.08)',
                              }}
                            >
                              <RejectIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {showPostToAttendance && req.status === 'approved' && (
                      <Tooltip
                        title={
                          req.attendance_posted_at
                            ? 'Již v docházce — kliknutím zapsat znovu'
                            : 'Zapsat do docházky'
                        }
                      >
                        <span>
                          <IconButton
                            color={req.attendance_posted_at ? 'success' : 'primary'}
                            disabled={decidingId === req.id}
                            onClick={() => void handlePostToAttendance(req)}
                            aria-label="Zapsat do docházky"
                            sx={actionBtnSx}
                          >
                            <PostAttendanceIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  };

  const maxTab = isPersonalistaRole ? (isItAdmin ? 1 : 0) : isVedouciRole ? 2 : 0;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <DateIcon fontSize="large" /> {DOVOLENA_MODULE_TITLE}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Role: {labelDovolenaUiRole(uiRole)}
          </Typography>
        </Box>
        {showCreateButton && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData((p) => ({
                ...p,
                request_type: 'dovolena',
                duvod: '',
                school_event_name: '',
                education_type: '',
                education_name: '',
                accreditation_number: '',
                notify_suplovani: true,
                date_from: '',
                date_to: '',
                year: String(new Date().getFullYear()),
                employment_relation: 'hlavni',
              }));
              setProxyEmployee(null);
              setSelectedDays([]);
              setCalendarMonth(new Date());
              setOpenDialog(true);
            }}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Nová žádost
          </Button>
        )}
      </Box>

      {isNastaveniRole && !shareToken && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.light',
              backgroundColor: '#eff6ff',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              Suplování (záskok výuky)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              E-mail osoby, která zajišťuje suplování (např. správce rozvrhu). Po schválení žádosti o
              nepřítomnost odešle zpráva, pokud žadatel (typicky pedagog) zaškrtne volbu suplování.
              Prázdné pole tuto možnost v žádostech vypne.
            </Typography>
            <TextField
              fullWidth
              type="email"
              size="small"
              value={suplovaniEmail}
              onChange={(e) => setSuplovaniEmail(e.target.value)}
              placeholder="např. bosak@iss-cheb.cz"
              sx={{ mb: 1.5, bgcolor: 'background.paper' }}
            />
            <Button
              variant="contained"
              onClick={() => void handleSaveSuplovaniEmail()}
              disabled={savingSuplovaniEmail}
            >
              {savingSuplovaniEmail ? 'Ukládání…' : '💾 Uložit e-mail pro suplování'}
            </Button>
            {suplovaniEmailMsg && (
              <Alert
                severity={
                  suplovaniEmailMsg.startsWith('Chyba') || suplovaniEmailMsg.startsWith('Zadejte')
                    ? 'error'
                    : 'success'
                }
                sx={{ mt: 1.5 }}
                onClose={() => setSuplovaniEmailMsg(null)}
              >
                {suplovaniEmailMsg}
              </Alert>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              Konfigurace vedoucích
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Vyberte role, které se budou zobrazovat v seznamu nadřízených při vytváření žádosti o
              dovolenou, neplaceném volnu nebo samostudiu.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 1,
                mb: 2,
              }}
            >
              {DOVOLENA_SUPERVISOR_ROLE_OPTIONS.map((role) => (
                <FormControlLabel
                  key={role.id}
                  control={
                    <Checkbox
                      checked={supervisorRoleIds.includes(role.id)}
                      onChange={() => toggleSupervisorRole(role.id)}
                      size="small"
                    />
                  }
                  label={role.label}
                  sx={{
                    m: 0,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                />
              ))}
            </Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => void handleSaveSupervisorRoles()}
              disabled={savingSupervisorRoles}
            >
              {savingSupervisorRoles ? 'Ukládání…' : '💾 Uložit nastavení vedoucích'}
            </Button>
            {supervisorRolesMsg && (
              <Alert
                severity={supervisorRolesMsg.startsWith('Chyba') ? 'error' : 'success'}
                sx={{ mt: 1.5 }}
                onClose={() => setSupervisorRolesMsg(null)}
              >
                {supervisorRolesMsg}
              </Alert>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              Správci docházky podle budovy
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ke každé budově můžete přiřadit jednoho zaměstnance. Po schválení žádosti přímým nadřízeným
              dostane tato osoba e-mail s informacemi o žadateli a schválených dnech. Pokud u budovy nikdo
              není přiřazen, e-mail se neodesílá.
            </Typography>
            {buildings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Žádné budovy v systému — nejprve je přidejte v administraci (Závady a požadavky).
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                {buildings.map((b) => {
                  const selectedId = buildingAttendanceAdmins[b.id] || '';
                  const selected =
                    attendanceEmployees.find((e) => String(e.id) === selectedId) || null;
                  return (
                    <Box
                      key={b.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {b.name}
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={attendanceEmployees}
                        value={selected}
                        onChange={(_, v) => setBuildingAttendanceAdmin(b.id, v ? String(v.id) : null)}
                        getOptionLabel={(o) =>
                          `${o.name}${o.email ? ` (${o.email})` : ''}${
                            o.personal_number ? ` · ${o.personal_number}` : ''
                          }`
                        }
                        isOptionEqualToValue={(a, c) => String(a.id) === String(c.id)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Správce docházky"
                            placeholder="Vyhledat zaměstnance…"
                          />
                        )}
                      />
                      {selectedId && !selected && (
                        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                          Uloženého zaměstnance se nepodařilo najít v aktuálním seznamu — vyberte znovu nebo
                          uložte prázdné pole.
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            <Button
              variant="contained"
              color="secondary"
              onClick={() => void handleSaveAttendanceAdmins()}
              disabled={savingAttendanceAdmins || buildings.length === 0}
            >
              {savingAttendanceAdmins ? 'Ukládání…' : '💾 Uložit správce docházky'}
            </Button>
            {attendanceAdminsMsg && (
              <Alert
                severity={attendanceAdminsMsg.startsWith('Chyba') ? 'error' : 'success'}
                sx={{ mt: 1.5 }}
                onClose={() => setAttendanceAdminsMsg(null)}
              >
                {attendanceAdminsMsg}
              </Alert>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              maxWidth: '100%',
              overflow: 'visible',
            }}
          >
            <Box sx={{ p: 2.5, pb: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
                Všechny žádosti v systému
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Kompletní přehled všech žádostí o nepřítomnost. Můžete filtrovat,
                řadit a mazat žádosti (smazané zůstanou v archivu).
              </Typography>
              <DovolenaRequestsToolbar
                nameQuery={approvalNameQuery}
                onNameQueryChange={setApprovalNameQuery}
                createdFrom={approvalCreatedFrom}
                onCreatedFromChange={setApprovalCreatedFrom}
                createdTo={approvalCreatedTo}
                onCreatedToChange={setApprovalCreatedTo}
                leaveFrom={approvalLeaveFrom}
                onLeaveFromChange={setApprovalLeaveFrom}
                leaveTo={approvalLeaveTo}
                onLeaveToChange={setApprovalLeaveTo}
                sortKey={approvalSortKey}
                onSortKeyChange={setApprovalSortKey}
                sortDir={approvalSortDir}
                onSortDirChange={setApprovalSortDir}
                disabled={loading}
                extendedFilters
                typeFilter={allListTypeFilter}
                onTypeFilterChange={setAllListTypeFilter}
                statusFilter={allListStatusFilter}
                onStatusFilterChange={setAllListStatusFilter}
                showProcessedFilter
                processedFilter={allListProcessedFilter}
                onProcessedFilterChange={setAllListProcessedFilter}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {settingsListTab === 0
                  ? `Zobrazeno ${allRequestsFiltered.length} z ${dashboard.allRequests?.length ?? 0} žádostí.`
                  : `Zobrazeno ${deletedRequestsFiltered.length} z ${dashboard.deletedRequests?.length ?? 0} smazaných.`}
              </Typography>
            </Box>
            <Tabs
              value={settingsListTab}
              onChange={(_, v) => setSettingsListTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 1, bgcolor: 'background.paper' }}
            >
              <Tab label={`Všechny (${dashboard.allRequests?.length ?? 0})`} />
              <Tab label={`Smazané (${dashboard.deletedRequests?.length ?? 0})`} />
            </Tabs>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box className="intranet-table-scroll" sx={{ p: 0 }}>
                {settingsListTab === 0 &&
                  renderRequestTable(allRequestsFiltered, {
                    colSpanEmpty: 10,
                    emptyLabel: 'Žádné žádosti neodpovídají filtru.',
                    showRequester: true,
                    showSupervisor: true,
                    showApprovalActions: false,
                    showPersonalistaProcessed: true,
                    showPostToAttendance: true,
                    showDeleteForOwner: true,
                  })}
                {settingsListTab === 1 &&
                  renderRequestTable(deletedRequestsFiltered, {
                    colSpanEmpty: 10,
                    emptyLabel: 'Žádné smazané žádosti neodpovídají filtru.',
                    showRequester: true,
                    showSupervisor: true,
                    showDeletedAt: true,
                  })}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {shareLoadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {shareLoadError}
        </Alert>
      )}

      {shareRequest && (
        <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Žádost z odkazu
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {shareRequest.requester_name} — {labelDovolenaRequestType(String(shareRequest.request_type || 'dovolena'))} —{' '}
            {format(parseYmdLocal(String(shareRequest.date_from).slice(0, 10)), 'dd.MM.yyyy')} až{' '}
            {format(parseYmdLocal(String(shareRequest.date_to).slice(0, 10)), 'dd.MM.yyyy')} (
            {shareRequest.total_hours} h) —{' '}
            {labelDovolenaEmploymentRelation(String(shareRequest.employment_relation || 'hlavni'))}
          </Typography>
          {shareRequest.days && shareRequest.days.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Rozpis:{' '}
              {shareRequest.days
                .map((d) => `${format(parseYmdLocal(d.day_date), 'dd.MM.yyyy')} (${formatDovolenaDayHours(d)})`)
                .join(', ')}
            </Typography>
          )}
          {(shareRequest.duvod || '').trim() !== '' && (
            <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              <strong>
                {dovolenaRequestTypeRequiresDuvod(String(shareRequest.request_type || 'dovolena'))
                  ? dovolenaDuvodFieldLabel(String(shareRequest.request_type))
                  : 'Poznámka'}
                :
              </strong>{' '}
              {shareRequest.duvod}
            </Typography>
          )}
          {(shareRequest.school_event_name || '').trim() !== '' && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Název akce školy:</strong> {shareRequest.school_event_name}
            </Typography>
          )}
          {(shareRequest.education_type || '').trim() !== '' && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Typ vzdělávání:</strong> {shareRequest.education_type}
            </Typography>
          )}
          {(shareRequest.education_name || '').trim() !== '' && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Název vzdělávání:</strong> {shareRequest.education_name}
            </Typography>
          )}
          {(shareRequest.accreditation_number || '').trim() !== '' && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Číslo akreditace:</strong> {shareRequest.accreditation_number}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {getStatusChip(shareRequest.status)}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => handleDownload(shareRequest.id)}
            >
              Stáhnout PDF
            </Button>
          </Box>
        </Paper>
      )}

      {!isNastaveniRole && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !isNastaveniRole ? (
        <>
          {isZadatelRole && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                Moje žádosti
              </Typography>
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <DovolenaRequestsToolbar
                  nameQuery={mineNameQuery}
                  onNameQueryChange={setMineNameQuery}
                  createdFrom={mineCreatedFrom}
                  onCreatedFromChange={setMineCreatedFrom}
                  createdTo={mineCreatedTo}
                  onCreatedToChange={setMineCreatedTo}
                  leaveFrom={mineLeaveFrom}
                  onLeaveFromChange={setMineLeaveFrom}
                  leaveTo={mineLeaveTo}
                  onLeaveToChange={setMineLeaveTo}
                  sortKey={mineSortKey}
                  onSortKeyChange={setMineSortKey}
                  sortDir={mineSortDir}
                  onSortDirChange={setMineSortDir}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Zobrazeno {mineRequestsFiltered.length} z {ownRequests.length} žádostí.
                </Typography>
              </Paper>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, mb: 4 }}>
                {renderRequestTable(mineRequestsFiltered, {
                  colSpanEmpty: 8,
                  emptyLabel: 'Zatím nemáte žádné vlastní žádosti.',
                  showRequester: false,
                  showSupervisor: true,
                  showApprovalActions: false,
                  showDeleteForOwner: true,
                })}
              </TableContainer>
            </>
          )}

          {isVedouciRole && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                Schvalování (nadřízený)
              </Typography>
              <Paper sx={{ borderRadius: 2, boxShadow: 2, mb: 4, maxWidth: '100%', overflow: 'visible' }}>
                <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <DovolenaRequestsToolbar
                    nameQuery={approvalNameQuery}
                    onNameQueryChange={setApprovalNameQuery}
                    createdFrom={approvalCreatedFrom}
                    onCreatedFromChange={setApprovalCreatedFrom}
                    createdTo={approvalCreatedTo}
                    onCreatedToChange={setApprovalCreatedTo}
                    leaveFrom={approvalLeaveFrom}
                    onLeaveFromChange={setApprovalLeaveFrom}
                    leaveTo={approvalLeaveTo}
                    onLeaveToChange={setApprovalLeaveTo}
                    sortKey={approvalSortKey}
                    onSortKeyChange={setApprovalSortKey}
                    sortDir={approvalSortDir}
                    onSortDirChange={setApprovalSortDir}
                  />
                </Box>
                <Tabs
                  value={Math.min(approvalTab, maxTab)}
                  onChange={(_, v) => setApprovalTab(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={`Ke schválení (${dashboard.pendingApproval.length})`} />
                  <Tab label={`Schválené (${dashboard.approvedByMe.length})`} />
                  <Tab label={`Zamítnuté (${dashboard.rejectedByMe.length})`} />
                </Tabs>
                <Box className="intranet-table-scroll" sx={{ p: 0, maxWidth: '100%' }}>
                  {approvalTab === 0 &&
                    renderRequestTable(filterApprovalRows(dashboard.pendingApproval), {
                      colSpanEmpty: 9,
                      emptyLabel: 'Žádné žádosti ke schválení.',
                      showRequester: true,
                      showSupervisor: false,
                      showApprovalActions: true,
                    })}
                  {approvalTab === 1 &&
                    renderRequestTable(filterApprovalRows(dashboard.approvedByMe), {
                      colSpanEmpty: 9,
                      emptyLabel: 'Zatím jste neschválil(a) žádnou žádost.',
                      showRequester: true,
                      showSupervisor: false,
                      showApprovalActions: false,
                    })}
                  {approvalTab === 2 &&
                    renderRequestTable(filterApprovalRows(dashboard.rejectedByMe), {
                      colSpanEmpty: 9,
                      emptyLabel: 'Žádné zamítnuté žádosti.',
                      showRequester: true,
                      showSupervisor: false,
                      showApprovalActions: false,
                    })}
                </Box>
              </Paper>

              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                Podané za zaměstnance
              </Typography>
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <DovolenaRequestsToolbar
                  nameQuery={mineNameQuery}
                  onNameQueryChange={setMineNameQuery}
                  createdFrom={mineCreatedFrom}
                  onCreatedFromChange={setMineCreatedFrom}
                  createdTo={mineCreatedTo}
                  onCreatedToChange={setMineCreatedTo}
                  leaveFrom={mineLeaveFrom}
                  onLeaveFromChange={setMineLeaveFrom}
                  leaveTo={mineLeaveTo}
                  onLeaveToChange={setMineLeaveTo}
                  sortKey={mineSortKey}
                  onSortKeyChange={setMineSortKey}
                  sortDir={mineSortDir}
                  onSortDirChange={setMineSortDir}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Žádosti, které jste podali jménem jiného zaměstnance. Zobrazeno{' '}
                  {onBehalfRequestsFiltered.length} z {onBehalfRequests.length}.
                </Typography>
              </Paper>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, mb: 4 }}>
                {renderRequestTable(onBehalfRequestsFiltered, {
                  colSpanEmpty: 9,
                  emptyLabel: 'Zatím jste nepodali žádnou žádost za zaměstnance.',
                  showRequester: true,
                  showSupervisor: true,
                  showApprovalActions: false,
                  showDeleteForOwner: true,
                })}
              </TableContainer>
            </>
          )}

          {isPersonalistaRole && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                Všechny žádosti
              </Typography>
              <Paper sx={{ borderRadius: 2, boxShadow: 2, maxWidth: '100%', overflow: 'visible' }}>
                <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <DovolenaRequestsToolbar
                    nameQuery={approvalNameQuery}
                    onNameQueryChange={setApprovalNameQuery}
                    createdFrom={approvalCreatedFrom}
                    onCreatedFromChange={setApprovalCreatedFrom}
                    createdTo={approvalCreatedTo}
                    onCreatedToChange={setApprovalCreatedTo}
                    leaveFrom={approvalLeaveFrom}
                    onLeaveFromChange={setApprovalLeaveFrom}
                    leaveTo={approvalLeaveTo}
                    onLeaveToChange={setApprovalLeaveTo}
                    sortKey={approvalSortKey}
                    onSortKeyChange={setApprovalSortKey}
                    sortDir={approvalSortDir}
                    onSortDirChange={setApprovalSortDir}
                  />
                </Box>
                {isItAdmin && (
                  <Tabs
                    value={Math.min(approvalTab, maxTab)}
                    onChange={(_, v) => setApprovalTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
                  >
                    <Tab label={`Všechny (${dashboard.allRequests?.length ?? 0})`} />
                    <Tab label={`Smazané (${dashboard.deletedRequests?.length ?? 0})`} />
                  </Tabs>
                )}
                <Box className="intranet-table-scroll" sx={{ p: 0 }}>
                  {(!isItAdmin || approvalTab === 0) && (
                    <>
                      <Box
                        sx={{
                          px: 2,
                          pt: 2,
                          pb: 1.5,
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: 'grey.50',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                          Další filtry
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                          <FormControl size="small" sx={{ minWidth: 160 }} disabled={loading}>
                            <InputLabel id="all-tab-type-label">Typ žádosti</InputLabel>
                            <Select
                              labelId="all-tab-type-label"
                              label="Typ žádosti"
                              value={allListTypeFilter}
                              onChange={(e) =>
                                setAllListTypeFilter(e.target.value as 'all' | DovolenaRequestType)
                              }
                            >
                              <MenuItem value="all">Všechny typy</MenuItem>
                              {DOVOLENA_REQUEST_TYPES.map((rt) => (
                                <MenuItem key={rt} value={rt}>
                                  {labelDovolenaRequestType(rt)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 140 }} disabled={loading}>
                            <InputLabel id="all-tab-status-label">Stav</InputLabel>
                            <Select
                              labelId="all-tab-status-label"
                              label="Stav"
                              value={allListStatusFilter}
                              onChange={(e) =>
                                setAllListStatusFilter(
                                  e.target.value as 'all' | 'new' | 'approved' | 'rejected',
                                )
                              }
                            >
                              <MenuItem value="all">Všechny stavy</MenuItem>
                              <MenuItem value="new">Čeká na schválení</MenuItem>
                              <MenuItem value="approved">Schváleno</MenuItem>
                              <MenuItem value="rejected">Zamítnuto</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 180 }} disabled={loading}>
                            <InputLabel id="all-tab-processed-label">Zpracováno</InputLabel>
                            <Select
                              labelId="all-tab-processed-label"
                              label="Zpracováno"
                              value={allListProcessedFilter}
                              onChange={(e) =>
                                setAllListProcessedFilter(
                                  e.target.value as 'all' | 'processed' | 'unprocessed',
                                )
                              }
                            >
                              <MenuItem value="all">Vše</MenuItem>
                              <MenuItem value="unprocessed">Nezpracované</MenuItem>
                              <MenuItem value="processed">Zpracované</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                          Zobrazeno {allRequestsFiltered.length} z {dashboard.allRequests?.length ?? 0} žádostí.
                        </Typography>
                      </Box>
                      {renderRequestTable(allRequestsFiltered, {
                        colSpanEmpty: 10,
                        emptyLabel: 'Žádné žádosti neodpovídají filtru.',
                        showRequester: true,
                        showSupervisor: true,
                        showApprovalActions: canApprovePrivilegedInAllTab,
                        showPersonalistaProcessed: user.role === 'personalista' || isItAdmin,
                        showPostToAttendance: user.role === 'personalista' || isItAdmin,
                        showDeleteForOwner: isItAdmin,
                      })}
                    </>
                  )}
                  {isItAdmin &&
                    approvalTab === 1 &&
                    renderRequestTable(filterApprovalRows(dashboard.deletedRequests ?? []), {
                      colSpanEmpty: 10,
                      emptyLabel: 'Žádné smazané žádosti.',
                      showRequester: true,
                      showSupervisor: true,
                      showDeletedAt: true,
                    })}
                </Box>
              </Paper>
            </>
          )}

          {isSpravceRozvrhuRole && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                Žádosti s potřebou suplování
              </Typography>
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <DovolenaRequestsToolbar
                  nameQuery={approvalNameQuery}
                  onNameQueryChange={setApprovalNameQuery}
                  createdFrom={approvalCreatedFrom}
                  onCreatedFromChange={setApprovalCreatedFrom}
                  createdTo={approvalCreatedTo}
                  onCreatedToChange={setApprovalCreatedTo}
                  leaveFrom={approvalLeaveFrom}
                  onLeaveFromChange={setApprovalLeaveFrom}
                  leaveTo={approvalLeaveTo}
                  onLeaveToChange={setApprovalLeaveTo}
                  sortKey={approvalSortKey}
                  onSortKeyChange={setApprovalSortKey}
                  sortDir={approvalSortDir}
                  onSortDirChange={setApprovalSortDir}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Jen žádosti, u kterých žadatel označil potřebu suplování. Zobrazeno{' '}
                  {suplovaniRequestsFiltered.length} z {dashboard.suplovaniRequests?.length ?? 0}.
                </Typography>
              </Paper>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, mb: 4 }}>
                {renderRequestTable(suplovaniRequestsFiltered, {
                  colSpanEmpty: 9,
                  emptyLabel: 'Žádné žádosti s potřebou suplování.',
                  showRequester: true,
                  showSupervisor: true,
                  showApprovalActions: false,
                })}
              </TableContainer>
            </>
          )}
        </>
      ) : null}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
            Nová žádost o nepřítomnost
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {canSubmitOnBehalf && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Žadatel (podat jménem zaměstnance)
                  </Typography>
                  <EmployeeUserPicker
                    value={proxyEmployee}
                    onChange={(u) => {
                      setProxyEmployee(u);
                      if (u) {
                        setFormData((prev) => ({ ...prev, requester_name: u.name }));
                      }
                    }}
                  />
                  {proxyEmployee && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Žádost bude podána jménem: <strong>{proxyEmployee.name}</strong>
                    </Typography>
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="dovolena-request-type-label">Typ žádosti</InputLabel>
                  <Select
                    labelId="dovolena-request-type-label"
                    label="Typ žádosti"
                    value={formData.request_type}
                    onChange={(e) => {
                      const next = e.target.value as DovolenaRequestType;
                      setFormData({
                        ...formData,
                        request_type: next,
                        school_event_name: '',
                        education_type: '',
                        education_name: '',
                        accreditation_number: '',
                      });
                    }}
                  >
                    {DOVOLENA_REQUEST_TYPES.map((rt) => (
                      <MenuItem key={rt} value={rt}>
                        {labelDovolenaRequestType(rt)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {dovolenaRequestTypeRequiresSchoolEvent(formData.request_type) && (
                <Grid item xs={12}>
                  <TextField
                    label="Název akce školy"
                    fullWidth
                    required
                    value={formData.school_event_name}
                    onChange={(e) =>
                      setFormData({ ...formData, school_event_name: e.target.value })
                    }
                    placeholder="Název akce školy"
                  />
                </Grid>
              )}
              {dovolenaRequestTypeRequiresEducation(formData.request_type) && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel id="dovolena-edu-type-label">Typ vzdělávání</InputLabel>
                      <Select
                        labelId="dovolena-edu-type-label"
                        label="Typ vzdělávání"
                        value={formData.education_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            education_type: e.target.value as
                              | ''
                              | 'akreditované'
                              | 'neakreditované',
                            accreditation_number: '',
                          })
                        }
                      >
                        <MenuItem value="akreditované">Akreditované</MenuItem>
                        <MenuItem value="neakreditované">Neakreditované</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {formData.education_type !== '' && (
                    <Grid item xs={12}>
                      <TextField
                        label="Název vzdělávání"
                        fullWidth
                        required
                        value={formData.education_name}
                        onChange={(e) =>
                          setFormData({ ...formData, education_name: e.target.value })
                        }
                        placeholder="Název vzdělávání"
                      />
                    </Grid>
                  )}
                  {formData.education_type === 'akreditované' && (
                    <Grid item xs={12}>
                      <TextField
                        label="Číslo akreditace"
                        fullWidth
                        required
                        value={formData.accreditation_number}
                        onChange={(e) =>
                          setFormData({ ...formData, accreditation_number: e.target.value })
                        }
                        placeholder="Číslo akreditace"
                      />
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  label={dovolenaDuvodFieldLabel(formData.request_type)}
                  fullWidth
                  required={dovolenaRequestTypeRequiresDuvod(formData.request_type)}
                  multiline
                  minRows={3}
                  value={formData.duvod}
                  onChange={(e) => setFormData({ ...formData, duvod: e.target.value })}
                  placeholder={dovolenaDuvodPlaceholder(formData.request_type)}
                  helperText={
                    dovolenaRequestTypeRequiresDuvod(formData.request_type)
                      ? formData.request_type === 'jine'
                        ? 'Poznámka je povinná pro typ „Jiné“.'
                        : 'Důvod je u tohoto typu povinný.'
                      : 'Volitelné — můžete doplnit bližší informace.'
                  }
                />
              </Grid>
              {suplovaniNotifyAvailable && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.notify_suplovani}
                        onChange={(e) =>
                          setFormData({ ...formData, notify_suplovani: e.target.checked })
                        }
                      />
                    }
                    label="Po schválení zaslat e-mail o potřebě suplování (záskok výuky)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                    Zaškrtněte, pokud jste pedagog a v uvedeném období je nutné zajistit suplování. U nepedagogických
                    zaměstnanců volbu nechte prázdnou.
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Osobní číslo"
                  fullWidth
                  required
                  value={formData.personal_number}
                  InputProps={{ readOnly: true }}
                  helperText={
                    formData.personal_number
                      ? 'Načteno ze seznamu zaměstnanců'
                      : 'Osobní číslo v systému chybí — kontaktujte personalistku'
                  }
                  placeholder="—"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Jméno"
                  fullWidth
                  required
                  value={formData.requester_name}
                  onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="budova-select-label">Budova</InputLabel>
                  <Select
                    labelId="budova-select-label"
                    value={formData.workplace}
                    label="Budova"
                    onChange={(e) =>
                      setFormData({ ...formData, workplace: e.target.value as string })
                    }
                    disabled={loadingBuildings}
                  >
                    {loadingBuildings ? (
                      <MenuItem disabled value="">
                        <em>Načítání…</em>
                      </MenuItem>
                    ) : buildings.length === 0 ? (
                      <MenuItem disabled value="">
                        <em>Žádné budovy nenalezeny</em>
                      </MenuItem>
                    ) : (
                      buildings.map((b) => (
                        <MenuItem key={b.id} value={b.name}>
                          {b.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Pracovní poměr</FormLabel>
                  <RadioGroup
                    row
                    value={formData.employment_relation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employment_relation: e.target.value as 'hlavni' | 'vedlejsi',
                      })
                    }
                  >
                    <FormControlLabel value="hlavni" control={<Radio />} label="Hlavní" />
                    <FormControlLabel value="vedlejsi" control={<Radio />} label="Vedlejší" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="supervisor-select-label">Vyberte svého přímého nadřízeného</InputLabel>
                  <Select
                    labelId="supervisor-select-label"
                    value={formData.supervisor_id}
                    label="Vyberte svého přímého nadřízeného"
                    onChange={(e) =>
                      setFormData({ ...formData, supervisor_id: e.target.value as string })
                    }
                    disabled={loadingSupervisors}
                  >
                    {loadingSupervisors ? (
                      <MenuItem disabled value="">
                        <em>Načítání...</em>
                      </MenuItem>
                    ) : supervisors.length === 0 ? (
                      <MenuItem disabled value="">
                        <em>Žádní vedoucí nenalezeni</em>
                      </MenuItem>
                    ) : (
                      supervisors.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name} ({s.role})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Rok"
                  type="text"
                  fullWidth
                  required
                  value={formData.year}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4 }}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      year: e.target.value.replace(/\D/g, '').slice(0, 4),
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Vyberte dny (1–8 hodin; pod 8 h uveďte začátek)
                </Typography>
                <DovolenaDaysCalendar
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  days={selectedDays}
                  onChange={(next) => {
                    setSelectedDays(next);
                    if (next.length > 0) {
                      const sorted = [...next].sort((a, b) => a.day_date.localeCompare(b.day_date));
                      setFormData((p) => ({
                        ...p,
                        year: String(parseYmdLocal(sorted[0].day_date).getFullYear()),
                      }));
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenDialog(false)}>Zrušit</Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={{ borderRadius: 2, px: 4 }}>
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Odeslat žádost'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={decisionTarget !== null}
        onClose={() => !decidingId && setDecisionTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {decisionTarget?.action === 'approve' ? 'Schválit žádost?' : 'Zamítnout žádost?'}
        </DialogTitle>
        <DialogContent>
          {decisionTarget && (
            <>
              <Typography sx={{ mb: 1.5 }}>
                {decisionTarget.action === 'approve'
                  ? 'Opravdu chcete tuto žádost schválit?'
                  : 'Opravdu chcete tuto žádost zamítnout?'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {decisionTarget.req.requester_name || '—'} ·{' '}
                {labelDovolenaRequestType(decisionTarget.req.request_type)} ·{' '}
                {format(parseYmdLocal(String(decisionTarget.req.date_from).slice(0, 10)), 'dd.MM.yyyy')}
                {' – '}
                {format(parseYmdLocal(String(decisionTarget.req.date_to).slice(0, 10)), 'dd.MM.yyyy')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            size="large"
            onClick={() => setDecisionTarget(null)}
            disabled={Boolean(decidingId)}
            sx={{ minHeight: 44, px: 3 }}
          >
            Zrušit
          </Button>
          <Button
            size="large"
            color={decisionTarget?.action === 'approve' ? 'success' : 'error'}
            variant="contained"
            startIcon={decisionTarget?.action === 'approve' ? <ApproveIcon /> : <RejectIcon />}
            onClick={() => void handleDecisionConfirm()}
            disabled={Boolean(decidingId)}
            sx={{ minHeight: 44, px: 3 }}
          >
            {decidingId ? (
              <CircularProgress size={22} color="inherit" />
            ) : decisionTarget?.action === 'approve' ? (
              'Ano, schválit'
            ) : (
              'Ano, zamítnout'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deletingId && setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Smazat žádost?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Opravdu chcete tuto žádost smazat? Žádost bude přesunuta do archivu smazaných a zmizí z vašeho
            přehledu.
          </Typography>
          {deleteTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {deleteTarget.requester_name || '—'} · {labelDovolenaRequestType(deleteTarget.request_type)} ·{' '}
              {format(parseYmdLocal(String(deleteTarget.date_from).slice(0, 10)), 'dd.MM.yyyy')}
              {' – '}
              {format(parseYmdLocal(String(deleteTarget.date_to).slice(0, 10)), 'dd.MM.yyyy')}
            </Typography>
          )}
          {deleteTarget && requestWantsSuplovani(deleteTarget) && (
            <Alert severity="warning">
              U této žádosti jste požádali o suplování. Po smazání je nutné telefonicky kontaktovat
              správce rozvrhů a domluvit se s ním na dalším postupu.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            size="large"
            onClick={() => setDeleteTarget(null)}
            disabled={Boolean(deletingId)}
            sx={{ minHeight: 44, px: 3 }}
          >
            Zrušit
          </Button>
          <Button
            size="large"
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={() => void handleDeleteConfirm()}
            disabled={Boolean(deletingId)}
            sx={{ minHeight: 44, px: 3 }}
          >
            {deletingId ? <CircularProgress size={22} color="inherit" /> : 'Ano, smazat'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DovolenaView;
